import React, { useState, useEffect, useRef } from 'react';
import { TextCompareService, DiffResult } from '../../lib/services/textCompareService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import './TextCompare.css';

export const TextCompare: React.FC = () => {
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [diffResult, setDiffResult] = useState<DiffResult | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleCompare = async () => {
    if (!leftText.trim() && !rightText.trim()) {
      setDiffResult(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${leftText.substring(0, 50)}-${rightText.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      const result = await TextCompareService.compare(leftText, rightText);

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setDiffResult(result.outputData as DiffResult);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, 'text-compare' as any);
        setError(errorMessage);
        setDiffResult(null);
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (leftText || rightText) {
      const timer = setTimeout(() => {
        handleCompare();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setDiffResult(null);
      setError(null);
      setIsLoading(false);
    }
  }, [leftText, rightText]);

  const handleCopy = async () => {
    if (!diffResult) return;

    try {
      const formatted = TextCompareService.formatDiff(diffResult);
      const success = await ClipboardUtils.copyText(formatted);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Summary';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!diffResult) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  const getLineClassName = (type: string) => {
    switch (type) {
      case 'added':
        return 'diff-line diff-added';
      case 'removed':
        return 'diff-line diff-removed';
      case 'modified':
        return 'diff-line diff-modified';
      default:
        return 'diff-line diff-unchanged';
    }
  };

  return (
    <div className="text-compare">
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>Text comparison requires JavaScript. Please enable JavaScript to use this tool.</p>
        </div>
      </noscript>

      <div style={{ marginBottom: '2rem' }}>
        <h1>Text Compare</h1>
        <p>
          Compare two texts side-by-side and see the differences.
          Highlights additions, deletions, and modifications line-by-line.
        </p>
      </div>

      <div className="compare-container">
        <div className="input-section">
          <div className="input-pane">
            <label>Left Text:</label>
            <textarea
              value={leftText}
              onChange={(e) => setLeftText(e.target.value)}
              placeholder="Enter first text..."
              data-testid="left-text"
            />
          </div>

          <div className="input-pane">
            <label>Right Text:</label>
            <textarea
              value={rightText}
              onChange={(e) => setRightText(e.target.value)}
              placeholder="Enter second text..."
              data-testid="right-text"
            />
          </div>
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        {diffResult && (
          <>
            <div className="stats-summary">
              <div className="stat-badge stat-added">
                <span className="stat-label">Added:</span>
                <span className="stat-value">{diffResult.addedCount}</span>
              </div>
              <div className="stat-badge stat-removed">
                <span className="stat-label">Removed:</span>
                <span className="stat-value">{diffResult.removedCount}</span>
              </div>
              <div className="stat-badge stat-modified">
                <span className="stat-label">Modified:</span>
                <span className="stat-value">{diffResult.modifiedCount}</span>
              </div>
              <div className="stat-badge stat-unchanged">
                <span className="stat-label">Unchanged:</span>
                <span className="stat-value">{diffResult.unchangedCount}</span>
              </div>
            </div>

            <div className="diff-section">
              <div className="diff-pane">
                <h3>Left (Original)</h3>
                <div className="diff-content">
                  {diffResult.leftLines.map((line, index) => (
                    <div key={index} className={getLineClassName(line.type)}>
                      <span className="line-number">{line.lineNumber}</span>
                      <span className="line-content">{line.content || ' '}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="diff-pane">
                <h3>Right (Comparison)</h3>
                <div className="diff-content">
                  {diffResult.rightLines.map((line, index) => (
                    <div key={index} className={getLineClassName(line.type)}>
                      <span className="line-number">{line.lineNumber}</span>
                      <span className="line-content">{line.content || ' '}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {isLoading && (
          <div className="loading-indicator">Processing...</div>
        )}

        <div className="button-container">
          <button
            ref={copyButtonRef}
            onClick={handleCopy}
            disabled={!diffResult}
            className={getButtonClassName()}
            data-testid="copy-button"
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};
